import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import TagSelect from '@components/fields/TagSelect';
import Form from '@components/forms/Form';
import {
  JourneyTagsMergeDocument,
  JourneyTagsMergeMutation,
  JourneyTagsMergeMutationVariables,
  TagTypeEnum
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type FormData = {
  mergeTags: string[];
  tag: string;
};

type Props = {
  defaultTag: string | undefined;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const JourneyTagsMergeForm = ({ defaultTag, onCancel, onError, onSuccess }: Props) => {
  const intl = useIntl();

  const defaultValues: FormData = {
    mergeTags: defaultTag ? [defaultTag] : [],
    tag: ''
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      mergeTags: yup
        .array()
        .defined()
        .of(yup.string().required({ id: 'errors.journeyTagsMerge.mergeTag.blank' }))
        .min(1, { id: 'errors.journeyTagsMerge.mergeTag.tooFew' }),
      tag: yup.string().required({ id: 'errors.journeyTagsMerge.tag.blank' })
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [journeyTagsMerge] = useMutation<JourneyTagsMergeMutation, JourneyTagsMergeMutationVariables>(
    JourneyTagsMergeDocument
  );

  const onSubmit = useCallback(() => {
    setBaseErrors([]);

    return journeyTagsMerge({
      variables: {
        mergeTags: form.getValues().mergeTags,
        tag: form.getValues().tag as string
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.journeyTagsMerge,
        intl,
        onError,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.journeyTagsMerge'
      });
    });
  }, [form, intl, journeyTagsMerge, onError, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <TagSelect<FormData>
        control={form.control}
        errors={form.formState.errors.mergeTags}
        label={intl.formatMessage({ id: 'forms.journeyTagsMerge.mergeTagsLabel' })}
        name="mergeTags"
        tagType={TagTypeEnum.JOURNEY}
      />
      <TagSelect<FormData>
        control={form.control}
        errors={form.formState.errors.tag}
        label={intl.formatMessage({ id: 'forms.journeyTagsMerge.tagLabel' })}
        name="tag"
        single
        tagType={TagTypeEnum.JOURNEY}
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveKey="forms.journeyTagsMerge.save" />
    </Form>
  );
};

export default JourneyTagsMergeForm;
